import type { SetStateAction } from "react";
import React from "react";

/**
 * Create a react context holding a given type
 *
 * @template T - Type stored in context
 * @return {*}  {(readonly [() => T | undefined)} - context generated as well as it's provider
 */
function createCtx<T>(defaultValue?: T): readonly [() => T | undefined, React.Provider<T | undefined>] {
  const ctx = React.createContext<T | undefined>(defaultValue);
  function useCtx() {
    const c = React.useContext(ctx);
    if (!c) throw new Error("Context must be inside a Provider with a value");
    return c;
  }
  return [useCtx, ctx.Provider] as const;
}

/**
 * Create a state-based react context
 *
 * @export
 * @template A - the type held in context
 * @param {A} defaultValue
 * @return {*}  {readonly [
 *   React.Context<{ state: A; update: React.Dispatch<SetStateAction<A>> }>}
 */
export function createStateCtx<A>(
  defaultValue: A
): readonly [
  React.Context<{ state: A; update: React.Dispatch<SetStateAction<A>> }>,
  (props: React.PropsWithChildren<unknown>) => React.ReactElement
] {
  type UpdateType = React.Dispatch<React.SetStateAction<typeof defaultValue>>;
  const defaultUpdate: UpdateType = () => defaultValue;
  const StateContext = React.createContext({ state: defaultValue, update: defaultUpdate });
  function Provider(props: React.PropsWithChildren<unknown>) {
    const [state, update] = React.useState(defaultValue);
    return <StateContext.Provider value={{ state, update }} {...props} />;
  }
  return [StateContext, Provider] as const;
}

/**
 * Create a reducer-based react context
 *
 * @export
 * @template StateType
 * @template ActionType
 * @param {React.Reducer<StateType, ActionType>} reducer
 * @param {StateType} initialState
 * @return {*}  {readonly [
 *   React.Context<{ state: StateType; dispatch: React.Dispatch<ActionType> }>}
 */
export function createReducerCtx<StateType, ActionType>(
  reducer: React.Reducer<StateType, ActionType>,
  initialState: StateType
): readonly [
  React.Context<{ state: StateType; dispatch: React.Dispatch<ActionType> }>,
  (props: React.PropsWithChildren<unknown>) => React.ReactElement
] {
  const defaultDispatch: React.Dispatch<ActionType> = () => initialState;
  const ctx = React.createContext({
    state: initialState,
    dispatch: defaultDispatch,
  });
  function Provider(props: React.PropsWithChildren<unknown>) {
    const [state, dispatch] = React.useReducer<React.Reducer<StateType, ActionType>>(reducer, initialState);
    return <ctx.Provider value={{ state, dispatch }} {...props} />;
  }
  return [ctx, Provider] as const;
}

export { createCtx };
